import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
  Alert,
  Chip
} from '@mui/material';
import axios from 'axios';

function AssessmentMode({ sessionId, onExit }) {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [questionNumber, setQuestionNumber] = useState(1);
  const [totalQuestions] = useState(10); // Fixed for MVP
  const [answers, setAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    fetchNextQuestion();
  }, []);

  const fetchNextQuestion = async () => {
    try {
      setLoading(true);
      setError('');
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const response = await axios.post(`${baseUrl}/api/question/next`);
      setCurrentQuestion(response.data.question);
      setSelectedAnswer('');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching question:', err);
      setError('Failed to load question. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAnswer) {
      setError('Please select an answer');
      return;
    }

    // Save answer
    const newAnswer = {
      questionId: currentQuestion.id,
      question: currentQuestion.question_text,
      selectedAnswer,
      correctAnswer: currentQuestion.correct_answer,
      isCorrect: selectedAnswer === currentQuestion.correct_answer
    };

    const updatedAnswers = [...answers, newAnswer];
    setAnswers(updatedAnswers);

    // Check if we've reached the end
    if (questionNumber >= totalQuestions) {
      setShowResults(true);
    } else {
      setQuestionNumber(questionNumber + 1);
      fetchNextQuestion();
    }
  };

  if (showResults) {
    const correctCount = answers.filter(a => a.isCorrect).length;
    const score = Math.round((correctCount / totalQuestions) * 100);

    return (
      <Container maxWidth="md" style={{ marginTop: '2rem' }}>
        <Card elevation={3}>
          <CardContent style={{ padding: '2rem' }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Assessment Complete!
            </Typography>

            <Box textAlign="center" marginY={3}>
              <Typography variant="h2" color="primary">
                {score}%
              </Typography>
              <Typography variant="h6" color="textSecondary">
                {correctCount} out of {totalQuestions} correct
              </Typography>
            </Box>

            <Box marginY={3}>
              <Typography variant="h6" gutterBottom>
                Your Answers:
              </Typography>
              {answers.map((answer, index) => (
                <Box
                  key={index}
                  padding={2}
                  marginY={1}
                  bgcolor={answer.isCorrect ? '#e8f5e9' : '#ffebee'}
                  borderRadius={2}
                >
                  <Typography variant="body2">
                    <strong>Q{index + 1}:</strong> {answer.question}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Your answer: {answer.selectedAnswer}
                    {!answer.isCorrect && (
                      <> (Correct: {answer.correctAnswer})</>
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={onExit}
              style={{ marginTop: '1rem' }}
            >
              Start New Assessment
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" style={{ marginTop: '2rem' }}>
        <Card elevation={3}>
          <CardContent style={{ padding: '2rem', textAlign: 'center' }}>
            <Typography variant="h6">Loading question...</Typography>
            <LinearProgress style={{ marginTop: '1rem' }} />
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!currentQuestion) {
    return (
      <Container maxWidth="md" style={{ marginTop: '2rem' }}>
        <Card elevation={3}>
          <CardContent style={{ padding: '2rem' }}>
            <Alert severity="error">No questions available</Alert>
            <Button
              fullWidth
              variant="contained"
              onClick={onExit}
              style={{ marginTop: '1rem' }}
            >
              Exit
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Parse wrong options (stored as JSON string in database)
  let options = [];
  try {
    const wrongOptions = typeof currentQuestion.wrong_options === 'string'
      ? JSON.parse(currentQuestion.wrong_options)
      : currentQuestion.wrong_options;
    options = [...wrongOptions, currentQuestion.correct_answer].sort(() => Math.random() - 0.5);
  } catch (err) {
    console.error('Error parsing options:', err);
    options = [currentQuestion.correct_answer];
  }

  return (
    <Container maxWidth="md" style={{ marginTop: '2rem' }}>
      <Card elevation={3}>
        <CardContent style={{ padding: '2rem' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
            <Chip
              label={`Question ${questionNumber} of ${totalQuestions}`}
              color="primary"
            />
            <LinearProgress
              variant="determinate"
              value={(questionNumber / totalQuestions) * 100}
              style={{ width: '200px' }}
            />
          </Box>

          <Typography variant="h5" component="h2" gutterBottom style={{ marginTop: '1rem' }}>
            {currentQuestion.question_text}
          </Typography>

          <FormControl component="fieldset" fullWidth style={{ marginTop: '2rem' }}>
            <RadioGroup
              value={selectedAnswer}
              onChange={(e) => setSelectedAnswer(e.target.value)}
            >
              {options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                  style={{
                    marginBottom: '0.5rem',
                    padding: '0.5rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px'
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {error && (
            <Alert severity="error" style={{ marginTop: '1rem' }}>
              {error}
            </Alert>
          )}

          <Box display="flex" gap={2} marginTop={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onExit}
            >
              Exit Assessment
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              disabled={!selectedAnswer}
            >
              {questionNumber >= totalQuestions ? 'Finish' : 'Next Question'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

export default AssessmentMode;
